import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { analyticsManager } from "../../../reducers/analytics.slice";
import { getAllOrderDetail } from "../../../reducers/order.slice";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { convertDateToCT, formatNumberToDollar } from "../../../utils/util";
import { Button } from "../../../components/ui/button";
import PageHeader from "../../../components/shared/PageHeader";
import StatCard from "../../../components/shared/StatCard";
import StatusBadge from "../../../components/shared/StatusBadge";
import SearchBar from "../../../components/shared/SearchBar";
import RefreshButton from "../../../components/shared/RefreshButton";
import DataTable from "../../../components/shared/DataTable";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const STAT_ACCENTS = ["#00ADB5", "#e8b84b", "#4caf50", "#af58f2", "#22d3ee", "#f27059"];
const CHUNK_SIZE = 50;

const keyToRouteMap = {
  pending_manager_approval: "/dashboard/pending-rsc-quotes",
  pending_member_approval: "/dashboard/pending-rsc-quotes",
  labels_pending: "/dashboard/shipment",
  shipment_pending: "/dashboard/shipment",
  payment_pending: "/dashboard/payouts",
  payment_ready: "/dashboard/payouts",
  early_payments: "/dashboard/payouts",
};

const permissionMapping = {
  pending_rsc_quotes: ["Pending Manager Approval", "Pending Member Approval"],
  shipping_and_tracking: [
    "Shipment Pending",
    "Labels Pending",
    "Payment Pending",
    "Payment Ready",
    "Early Payments",
  ],
};

const ManagerDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const user = getUsers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { userDetails, showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const getRowsPerPage = sessionStorage.getItem("managerDashboard_rowsPerPage");
    return getRowsPerPage ? JSON.parse(getRowsPerPage) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const isInitialRender = useRef(true);

  useEffect(() => {
    const payload = {
      userId: user?.id || user?.id,
      access_token: userDetails?.access_token || user?.access_token,
    };

    dispatch(analyticsManager(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      if (res?.payload) {
        const dataArray = Object.keys(res.payload).map((key) => ({
          key,
          title: key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
          value: res.payload[key],
          route: keyToRouteMap[key] || "/",
        }));

        setAnalyticsData(dataArray);
      } else {
        console.error("No payload in response:", res);
      }
    });
  }, [dispatch, user?.id]);

  const managementAnalytics = Object.keys(permissionMapping)
    .filter((permission) => {
      const hasPermission = user?.permission_details?.[permission];
      return hasPermission;
    })
    .flatMap((permission) => permissionMapping[permission])
    .map((title) => {
      const card = analyticsData.find((data) => data.title === title);
      return card || null;
    })
    .filter((card) => card !== null && card.value !== undefined);

  const loadDataChunk = async (chunkNumber = 0) => {
    const filterKey = JSON.stringify({
      search: searchTerm,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    if (pageCache.has(cacheKey)) {
      const cachedData = pageCache.get(cacheKey);
      if (cachedData.totalRows !== undefined) {
        setTotalRows(cachedData.totalRows);
      }
      return cachedData.data;
    }

    if (loadingChunk) {
      return [];
    }

    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) {
      setShowLoader(true);
    }

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
      };

      if (searchTerm && searchTerm?.trim() !== "") {
        payload.search = searchTerm?.trim();
      }

      const res = await dispatch(getAllOrderDetail(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setHasMoreData(false);
        setFilteredData([]);
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return [];
      }
      if (!res?.payload || !res?.payload?.data || !Array.isArray(res.payload.data)) {
        console.log("No valid data in response");
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }

      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) {
        setTotalRows(res?.payload?.total);
      }
      const formattedOrder = res?.payload?.data?.map((item) => {
        const createdDate = convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm:ss A");
        const orderPrice = formatNumberToDollar(item?.order_price);
        const newOrderItems = item?.order_items?.map((orderItem) => {
          const buy_price = formatNumberToDollar(orderItem?.buy_price || 0);
          const cashout_price = formatNumberToDollar(orderItem?.cashout_price || 0);
          const total_amount = formatNumberToDollar(orderItem?.total_amount || 0);
          return {
            ...orderItem,
            buy_price,
            cashout_price,
            total_amount,
          };
        });
        return {
          ...item,
          createdDate,
          orderPrice,
          newOrderItems,
        };
      });

      setPageCache((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, {
          data: formattedOrder,
          timestamp: Date.now(),
          totalRows: res?.payload?.total,
        });
        return next;
      });

      const hasMore = formattedOrder?.length === CHUNK_SIZE;
      setHasMoreData(hasMore);
      return formattedOrder;
    } catch (error) {
      console.error("Error in loadDataChunk:", error);
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
      setShowLoader(false);
    }
  };

  const columns = [
    {
      key: "id",
      label: "Order ID",
      width: "16%",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.id}</span>,
    },
    {
      key: "orderPrice",
      label: "Order Price",
      width: "12%",
      align: "right",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.orderPrice}</span>,
    },
    { key: "type", label: "Order Type", width: "14%" },
    { key: "created_by_name", label: "Member Name", width: "18%" },
    { key: "createdDate", label: "Order Date", width: "20%" },
    {
      key: "status",
      label: "Status",
      width: "20%",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const itemsColumns = [
    { key: "id", label: "Item ID" },
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quote_id", label: "Quote ID" },
    { key: "quantity", label: "Quantity", align: "right" },
    { key: "buy_price", label: "Buy Price", align: "right" },
    { key: "cashout_price", label: "Cashout Price", align: "right" },
    { key: "retailer_name", label: "Retailer Name" },
    { key: "total_amount", label: "Total Amount", align: "right" },
    { key: "notes", label: "Notes" },
    {
      key: "status",
      label: "Status",
      align: "center",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const loadInitialData = async () => {
    const initialChunk = await loadDataChunk(0);
    setAllLoadedData(initialChunk || []);
  };

  const checkAndLoadMoreData = async (page, rowsPerPageValue) => {
    const requiredDataLength = (page + 1) * rowsPerPageValue;
    const currentDataLength = allLoadedData.length;

    if (currentDataLength >= requiredDataLength) return;
    if (!hasMoreData) return;
    if (loadingChunk) return;

    if (totalRows > 0 && currentDataLength >= totalRows) {
      setHasMoreData(false);
      return;
    }

    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(currentDataLength / CHUNK_SIZE);
      const targetPage = page;
      const newData = await loadDataChunk(nextChunk);
      if (newData && newData?.length > 0) {
        setAllLoadedData((prev) => [...prev, ...newData]);

        setTimeout(() => {
          const startIndex = page * rowsPerPageValue;
          const endIndex = startIndex + rowsPerPageValue;
          const combinedData = [...allLoadedData, ...newData];
          const pageData = combinedData.slice(startIndex, endIndex);
          setCurrentPage(targetPage);
          setFilteredData(pageData);
        }, 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      showToast("Error loading data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangePage = async (event, newPage) => {
    if (isNavigatingToPage || loadingChunk) return;

    const requiredDataLength = (newPage + 1) * rowsPerPage;

    try {
      setIsNavigatingToPage(true);

      if (allLoadedData.length < requiredDataLength && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const pageData = allLoadedData.slice(startIndex, endIndex);
        setCurrentPage(newPage);
        setFilteredData(pageData);
      }
    } catch (error) {
      console.error("Error changing page:", error);
      showToast("Error loading page data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) return;
    if (!allLoadedData?.length) return;

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageData = allLoadedData.slice(startIndex, endIndex);
    if (pageData.length > 0 && !isNavigatingToPage && !loadingChunk) {
      setFilteredData(pageData);
    } else if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [rowsPerPage, allLoadedData]);

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("managerDashboard_rowsPerPage", JSON.stringify(newRowsPerPage));
    setCurrentPage(0);

    if (allLoadedData.length < newRowsPerPage && hasMoreData && !loadingChunk) {
      checkAndLoadMoreData(0, newRowsPerPage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (isSearching) return;

    const performSearch = async () => {
      try {
        setIsSearching(true);
        setCurrentPage(0);
        setAllLoadedData([]);

        const results = await loadDataChunk(0);
        if (results?.length > 0) {
          setAllLoadedData(results);
        } else {
          setFilteredData([]);
          setAllLoadedData([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;

    setPageCache(new Map());
    setAllLoadedData([]);
    setHasMoreData(true);

    loadInitialData().then(() => {
      if (currentPageBeforeRefresh > 0) {
        setTimeout(() => {
          setCurrentPage(currentPageBeforeRefresh);
        }, 100);
      }
    });
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: isMobile ? 24 : 30 }}>
        <PageHeader title={`Welcome ${user?.first_name || ""}`} subtitle="Review management queues and recent order activity." />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(auto-fit, minmax(145px, 1fr))" : "repeat(auto-fit, minmax(190px, 1fr))",
          gap: isMobile ? 12 : 16,
          marginBottom: isMobile ? 24 : 34,
        }}
      >
        {managementAnalytics.map((data, index) => (
          <StatCard
            key={data.key}
            label={data.title}
            value={data.value}
            accent={STAT_ACCENTS[index % STAT_ACCENTS.length]}
            actionLabel={data.route !== "/" ? "view" : undefined}
            onClick={data.route !== "/" ? () => navigate(data.route) : undefined}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: isMobile ? 12 : 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0 }}>
            Orders Overview
          </h2>
          <p style={{ fontSize: 12.5, color: "#c3c8cc", margin: "3px 0 0" }}>
            Manage and track orders.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            flexWrap: "wrap",
            flexShrink: isMobile ? 1 : 0,
            width: isMobile ? "100%" : "auto",
            gap: isMobile ? 10 : 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: isMobile ? 1 : 0,
              width: isMobile ? "100%" : "auto",
              gap: isMobile ? 8 : 12,
            }}
          >
            {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedTerm={setDebouncedSearch}
              />
            </div>
            {isMobile && (
              <Button
                variant="link"
                onClick={() => navigate("/dashboard/orders")}
                className="tw-p-0 tw-h-auto"
                style={{
                  flexShrink: 0,
                  textDecoration: "none",
                  minHeight: 34,
                  padding: "0 2px",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                View all
                <ArrowRight size={13} style={{ marginLeft: 6 }} />
              </Button>
            )}
          </div>

          {!isMobile && (
            <Button
              variant="link"
              onClick={() => navigate("/dashboard/orders")}
              className="tw-p-0 tw-h-auto"
              style={{ flexShrink: 0, textDecoration: "none", marginLeft: 32 }}
            >
              View all
              <ArrowRight size={13} style={{ marginLeft: 6 }} />
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        nestedColumns={itemsColumns}
        nestedRowKey="newOrderItems"
        nestedTableHeader="Order Items"
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => handleChangePage(null, newPage)}
        onRowsPerPageChange={(newValue) => handleChangeRowsPerPage({ target: { value: newValue } })}
        loading={showLoader || loadingChunk || isNavigatingToPage}
        minWidth={940}
      />
    </div>
  );
};

export default ManagerDashboard;
